/*
 * Attribuzione traffico 3DLab.
 *
 * Cattura first touch e last non-direct touch (UTM, gclid, srsltid, referrer
 * esterno), li conserva in localStorage e li copia negli attributi del carrello
 * (__3dlab_*), cosi' arrivano agli attributi privati dell'ordine Shopify.
 * Le navigazioni interne (prodotto -> configuratore -> prodotto custom ->
 * carrello) non diventano mai una nuova sorgente.
 */
(() => {
  'use strict';

  if (window.ThreeDLabAttribution) return;
  // Nell'editor del tema i dati sarebbero rumore.
  if (window.Shopify && window.Shopify.designMode) return;

  const STORAGE_KEY = '3dlab_attribution_v1';
  const CHECK_KEY = '3dlab_attribution_cart_check';
  const CLARITY_TAGS_KEY = '3dlab_attribution_clarity_tags';
  const ATTRIBUTE_PREFIX = '__3dlab_';
  const ATTRIBUTION_TTL_MS = 90 * 24 * 60 * 60 * 1000;
  const SESSION_TTL_MS = 30 * 60 * 1000;
  const CART_CHECK_TTL_MS = 10 * 60 * 1000;
  const MAX_VALUE_LENGTH = 255;
  const TOUCH_FIELDS = ['source', 'medium', 'campaign', 'content', 'term'];

  // Host che non rappresentano mai una sorgente marketing: sito, checkout, pagamenti.
  const INTERNAL_HOST_SUFFIXES = [
    'shop3dlab.it',
    'shop3dlab.cloud',
    'myshopify.com',
    'shopify.com',
    'shop.app',
    'paypal.com'
  ];
  const SOCIAL_SOURCES = [
    [/(^|\.)instagram\.com$/, 'instagram'],
    [/(^|\.)(facebook\.com|fb\.com|fb\.me)$/, 'facebook'],
    [/(^|\.)(twitter\.com|x\.com|t\.co)$/, 'twitter'],
    [/(^|\.)(linkedin\.com|lnkd\.in)$/, 'linkedin'],
    [/(^|\.)pinterest\.[a-z.]+$/, 'pinterest'],
    [/(^|\.)tiktok\.com$/, 'tiktok'],
    [/(^|\.)(youtube\.com|youtu\.be)$/, 'youtube'],
    [/(^|\.)reddit\.com$/, 'reddit'],
    [/(^|\.)(wa\.me|whatsapp\.com)$/, 'whatsapp'],
    [/(^|\.)(t\.me|telegram\.org)$/, 'telegram']
  ];
  const SEARCH_SOURCE_PATTERN = /^(?:www\.|search\.|m\.)?(google|bing|duckduckgo|ecosia|qwant|startpage|yandex|baidu|yahoo|brave)\./;

  let memoryRecord = null;
  let visitApplied = false;

  function cleanValue(value, maxLength = MAX_VALUE_LENGTH) {
    if (value === undefined || value === null) return '';
    return String(value).trim().slice(0, maxLength);
  }

  function cleanLower(value) {
    return cleanValue(value, 100).toLowerCase();
  }

  function readRaw() {
    for (const area of ['localStorage', 'sessionStorage']) {
      try {
        const raw = window[area].getItem(STORAGE_KEY);
        if (raw) return JSON.parse(raw);
      } catch (_) {
        // Storage non disponibile (privacy mode): si prova l'area successiva.
      }
    }
    return memoryRecord;
  }

  function writeRecord(record) {
    memoryRecord = record;
    const serialized = JSON.stringify(record);
    for (const area of ['localStorage', 'sessionStorage']) {
      try {
        window[area].setItem(STORAGE_KEY, serialized);
        return;
      } catch (_) {
        // Quota o storage bloccato: fallback all'area successiva e poi alla memoria.
      }
    }
  }

  function readRecord(now = Date.now()) {
    const record = readRaw();
    if (!record || typeof record !== 'object' || record.v !== 1) return null;
    const anchor = Number(record.first?.ts || record.last?.ts || 0);
    if (anchor && now - anchor > ATTRIBUTION_TTL_MS) {
      // Finestra di attribuzione scaduta: si riparte, mantenendo solo la sessione.
      return { v: 1, session: record.session };
    }
    return record;
  }

  function getNavigationType() {
    try {
      const entry = performance.getEntriesByType('navigation')[0];
      if (entry && entry.type) return entry.type;
    } catch (_) {
      // Navigation Timing non disponibile.
    }
    return 'navigate';
  }

  function parseHost(url) {
    try {
      return new URL(url, window.location.href);
    } catch (_) {
      return null;
    }
  }

  function isInternalHost(host) {
    const hostname = String(host || '').toLowerCase();
    if (!hostname) return true;
    if (hostname === window.location.hostname.toLowerCase()) return true;
    return INTERNAL_HOST_SUFFIXES.some(
      (suffix) => hostname === suffix || hostname.endsWith(`.${suffix}`)
    );
  }

  function getExternalReferrer() {
    const parsed = document.referrer ? parseHost(document.referrer) : null;
    if (!parsed || isInternalHost(parsed.hostname)) return null;
    return parsed;
  }

  function classifyReferrer(referrer) {
    const host = referrer.hostname.toLowerCase().replace(/^www\./, '');
    for (const [pattern, source] of SOCIAL_SOURCES) {
      if (pattern.test(host)) return { source, medium: 'social' };
    }
    const search = SEARCH_SOURCE_PATTERN.exec(referrer.hostname.toLowerCase());
    if (search) return { source: search[1], medium: 'organic' };
    return { source: host, medium: 'referral' };
  }

  // Restituisce il touch di marketing della visita corrente, oppure null se e' direct/interna.
  function detectTouch(params, referrer) {
    const utm = {
      source: cleanLower(params.get('utm_source')),
      medium: cleanLower(params.get('utm_medium')),
      campaign: cleanValue(params.get('utm_campaign'), 100),
      content: cleanValue(params.get('utm_content'), 100),
      term: cleanValue(params.get('utm_term'), 100)
    };
    const hasUtm = TOUCH_FIELDS.some((field) => utm[field]);

    if (hasUtm) {
      const fromReferrer = referrer ? classifyReferrer(referrer) : null;
      return {
        ...utm,
        source: utm.source || fromReferrer?.source || '(not set)',
        medium: utm.medium || '(not set)'
      };
    }
    if (params.get('gclid')) return { source: 'google', medium: 'cpc' };
    if (params.get('srsltid')) return { source: 'google', medium: 'organic' };
    if (referrer) return classifyReferrer(referrer);
    return null;
  }

  function createId() {
    if (window.crypto && typeof window.crypto.randomUUID === 'function') {
      return window.crypto.randomUUID();
    }
    return `s-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 12)}`;
  }

  function touchSnapshot(touch, now) {
    const snapshot = { ts: now };
    TOUCH_FIELDS.forEach((field) => {
      const value = cleanValue(touch[field], 100);
      if (value) snapshot[field] = value;
    });
    return snapshot;
  }

  function observeVisit() {
    const params = new URL(window.location.href).searchParams;
    const navigationType = getNavigationType();
    const referrer = getExternalReferrer();
    return {
      now: Date.now(),
      // Reload e back/forward ripresentano URL/referrer originali: non sono un nuovo touch.
      isRepeatNavigation: navigationType === 'reload' || navigationType === 'back_forward',
      touch: detectTouch(params, referrer),
      gclid: cleanValue(params.get('gclid'), 200),
      srsltid: cleanValue(params.get('srsltid'), 200),
      landing: cleanValue(window.location.pathname),
      referrer: referrer ? cleanValue(referrer.origin + referrer.pathname) : '',
      hasAnyReferrer: Boolean(document.referrer)
    };
  }

  const visit = observeVisit();

  function applyVisit(record) {
    if (visit.isRepeatNavigation && record.first) return;

    if (visit.gclid) record.gclid = visit.gclid;
    if (visit.srsltid) record.srsltid = visit.srsltid;

    if (visit.touch) {
      record.last = touchSnapshot(visit.touch, visit.now);
      if (!record.first) {
        record.first = {
          ...touchSnapshot(visit.touch, visit.now),
          landing: visit.landing,
          referrer: visit.referrer
        };
      }
    } else if (!record.first && !visit.hasAnyReferrer) {
      // Prima visita senza parametri ne' referrer: direct reale.
      record.first = {
        ts: visit.now,
        source: '(direct)',
        medium: '(none)',
        landing: visit.landing,
        referrer: ''
      };
    }
    // Referrer interno senza dati salvati: stato perso a meta' navigazione,
    // meglio non registrare nulla che un falso "direct".
  }

  // Scrive la visita corrente nello storage (una sola volta per caricamento pagina).
  function persist() {
    const now = Date.now();
    const record = readRecord(now) || { v: 1 };

    if (!visitApplied) {
      visitApplied = true;
      applyVisit(record);
    }

    const session = record.session;
    if (session && session.id && now - Number(session.ts || 0) <= SESSION_TTL_MS) {
      session.ts = now;
    } else {
      record.session = { id: createId(), ts: now };
    }

    writeRecord(record);
    return record;
  }

  function buildAttributes(record) {
    const attributes = {};
    const put = (key, value) => {
      const clean = cleanValue(value);
      if (clean) attributes[ATTRIBUTE_PREFIX + key] = clean;
    };

    TOUCH_FIELDS.forEach((field) => {
      put(`first_${field}`, record?.first?.[field]);
      put(`last_${field}`, record?.last?.[field]);
    });
    put('gclid', record?.gclid);
    put('srsltid', record?.srsltid);
    put('landing_page', record?.first?.landing);
    put('referrer', record?.first?.referrer);
    put('session_id', record?.session?.id);

    return attributes;
  }

  function getAttributes() {
    return buildAttributes(readRecord());
  }

  function routeUrl(routeName, fallback) {
    const base = (window.routes && window.routes[routeName]) || fallback;
    const normalized = String(base).replace(/\/$/, '');
    return normalized.endsWith('.js') ? normalized : `${normalized}.js`;
  }

  function hasCartCookie() {
    return document.cookie.split(';').some((cookie) => cookie.trim().startsWith('cart='));
  }

  function readCartCookie() {
    const match = document.cookie.split(';').map((cookie) => cookie.trim())
      .find((cookie) => cookie.startsWith('cart='));
    return match ? match.slice(5) : '';
  }

  // Allinea gli attributi del carrello a quelli desiderati. Scrive solo le differenze,
  // non invia valori vuoti e azzera (valore '') soltanto chiavi __3dlab_ ormai obsolete.
  async function syncAttributes(desired) {
    const cartResponse = await fetch(routeUrl('cart_url', '/cart'), {
      method: 'GET',
      headers: { 'Accept': 'application/json' },
      credentials: 'same-origin',
      cache: 'no-store'
    });
    if (!cartResponse.ok) return false;
    const cart = await cartResponse.json();
    const current = cart.attributes || {};

    const changes = {};
    Object.entries(desired).forEach(([key, value]) => {
      if (current[key] !== value) changes[key] = value;
    });
    Object.keys(current).forEach((key) => {
      if (key.startsWith(ATTRIBUTE_PREFIX) && !(key in desired)) changes[key] = '';
    });
    if (!Object.keys(changes).length) return false;

    const updateResponse = await fetch(routeUrl('cart_update_url', '/cart/update'), {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
      credentials: 'same-origin',
      body: JSON.stringify({ attributes: changes })
    });
    return updateResponse.ok;
  }

  async function syncToCart() {
    persist();
    return syncAttributes(buildAttributes(readRecord()));
  }

  let inFlightSync = null;
  function syncToCartOnce() {
    if (!inFlightSync) {
      inFlightSync = syncToCart().finally(() => { inFlightSync = null; });
    }
    return inFlightSync;
  }

  function syncOnPageLoad() {
    if (!hasCartCookie()) return;

    const signature = `${readCartCookie()}|${JSON.stringify(getAttributes())}`;
    try {
      const previous = JSON.parse(sessionStorage.getItem(CHECK_KEY) || 'null');
      if (previous && previous.signature === signature && Date.now() - previous.ts < CART_CHECK_TTL_MS) return;
    } catch (_) {
      // Senza sessionStorage si controlla a ogni pagina.
    }

    syncToCartOnce()
      .then(() => {
        try {
          sessionStorage.setItem(CHECK_KEY, JSON.stringify({ signature, ts: Date.now() }));
        } catch (_) {
          // Il throttle e' solo un'ottimizzazione.
        }
      })
      .catch(() => {});
  }

  // Tag Clarity di sessione: aggiornati solo se cambiano.
  function applyClarityTags(record = readRecord()) {
    if (typeof window.clarity !== 'function' || !record) return;

    const tags = {
      traffic_first_source: record.first?.source,
      traffic_last_source: record.last?.source
    };
    let applied = {};
    try {
      applied = JSON.parse(sessionStorage.getItem(CLARITY_TAGS_KEY) || '{}');
    } catch (_) {
      applied = {};
    }

    Object.entries(tags).forEach(([key, value]) => {
      const clean = cleanValue(value, 100);
      if (!clean || applied[key] === clean) return;
      try {
        window.clarity('set', key, clean);
        applied[key] = clean;
      } catch (_) {
        // Clarity non e' pronto: si riprova al prossimo caricamento.
      }
    });

    try {
      sessionStorage.setItem(CLARITY_TAGS_KEY, JSON.stringify(applied));
    } catch (_) {
      // Solo ottimizzazione.
    }
  }

  function refresh() {
    const record = persist();
    applyClarityTags(record);
    syncOnPageLoad();
  }

  window.ThreeDLabAttribution = {
    getAttributes,
    syncToCart: syncToCartOnce,
    applyClarityTags
  };

  // Un nuovo carrello (o un add-to-cart) deve ricevere gli attributi subito.
  if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
    subscribe(PUB_SUB_EVENTS.cartUpdate, () => {
      syncToCartOnce().catch(() => {});
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', refresh, { once: true });
  } else {
    refresh();
  }
  window.addEventListener('load', () => applyClarityTags(), { once: true });
  // Sul configuratore Clarity arriva dopo il primo render (content_for_header differito).
  window.addEventListener('threedlab:analytics-ready', () => applyClarityTags(), { once: true });
})();
